/**
 * Unified HTTP Client based on fetch API
 * Replaces axios with native fetch for consistency
 * Follows KISS and YAGNI principles
 */

import { createLogger } from "@/utils/logger";

const logger = createLogger("http-client");

// Core types
export interface RequestConfig {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, any>;
  cache?: RequestCache;
  credentials?: RequestCredentials;
  timeout?: number;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  meta?: {
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    timing?: {
      requestId: string;
      duration: number;
      timestamp: string;
    };
  };
}

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

// Configuration - optimized for build vs runtime
// FIXED: Use NEXT_PUBLIC_BASE_URL for API calls as per CLAUDE.md guidelines
const isDevelopment = 
  process.env.NODE_ENV === "development" || 
  process.env.NEXT_PUBLIC_ENV === "development";

// Debug timeout configuration - use NEXT_PUBLIC_ version for client-side access
const apiTimeout = parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || process.env.API_TIMEOUT || "30000") || 30000;
const calculatedTimeout = isDevelopment
  ? apiTimeout
  : process.env.NEXT_BUILD_MODE === "true"
    ? 10000
    : 60000; // Increased production timeout

// Log timeout configuration in development for debugging
if (isDevelopment) {
  console.log("🔧 HTTP Client Debug:", {
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
    API_TIMEOUT: process.env.API_TIMEOUT,
    NEXT_PUBLIC_API_TIMEOUT: process.env.NEXT_PUBLIC_API_TIMEOUT,
    isDevelopment,
    apiTimeout,
    calculatedTimeout,
  });
}

const DEFAULT_CONFIG = {
  baseURL: process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:3000", // API backend URL
  timeout: calculatedTimeout, // Use calculated timeout with debugging
  headers: {
    "Content-Type": "application/json",
    // Remove problematic header causing CORS issues
    // "x-content-type-options": "nosniff", // REMOVED: causing CORS preflight errors
  },
  credentials: "include" as RequestCredentials,
} as const;

/**
 * Unified HTTP Client class
 * Single responsibility: HTTP communications
 */
export class HttpClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private defaultTimeout: number;
  private defaultCredentials: RequestCredentials;

  constructor(config: Partial<typeof DEFAULT_CONFIG> = {}) {
    this.baseURL = config.baseURL || DEFAULT_CONFIG.baseURL;
    this.defaultHeaders = { ...DEFAULT_CONFIG.headers, ...config.headers };
    this.defaultTimeout = config.timeout || DEFAULT_CONFIG.timeout;
    this.defaultCredentials = config.credentials || DEFAULT_CONFIG.credentials;
  }

  /**
   * Build URL with query parameters
   * FIXED: Правильная конструкция URL для Next.js API routes
   */
  private buildURL(endpoint: string, params?: Record<string, any>): string {
    let finalUrl: URL;

    try {
      // Обработка абсолютных URL (например, полные URL бэкенда)
      if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
        finalUrl = new URL(endpoint);
      } else {
        // Обработка относительных URL
        let baseUrl = this.baseURL;

        // Убеждаемся, что baseURL заканчивается слешем для правильной конкатенации
        if (!baseUrl.endsWith("/")) {
          baseUrl += "/";
        }

        // Убираем начальный слеш из endpoint, если он есть, чтобы избежать двойных слешей
        let cleanEndpoint = endpoint;
        if (cleanEndpoint.startsWith("/")) {
          cleanEndpoint = cleanEndpoint.substring(1);
        }

        // Конструируем итоговый URL
        const fullUrl = baseUrl + cleanEndpoint;
        finalUrl = new URL(fullUrl);
      }
    } catch (error) {
      // Fallback: если конструкция URL не удалась
      const origin =
        typeof window !== "undefined"
          ? window.location.origin
          : "http://localhost:3001";
      const fallbackBase = this.baseURL.startsWith("/")
        ? `${origin}${this.baseURL}`
        : this.baseURL;
      const cleanBase = fallbackBase.endsWith("/")
        ? fallbackBase
        : `${fallbackBase}/`;
      const cleanEndpoint = endpoint.startsWith("/")
        ? endpoint.substring(1)
        : endpoint;
      finalUrl = new URL(cleanBase + cleanEndpoint);
    }

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              finalUrl.searchParams.append(`${key}[${index}]`, String(item));
            });
          } else {
            finalUrl.searchParams.append(key, String(value));
          }
        }
      });
    }

    return finalUrl.toString();
  }

  /**
   * Prepare request headers
   */
  private prepareHeaders(customHeaders?: Record<string, string>): Headers {
    const headers = new Headers();

    // Add default headers
    Object.entries(this.defaultHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });

    // Add custom headers
    if (customHeaders) {
      Object.entries(customHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });
    }

    return headers;
  }

  /**
   * Prepare request body
   */
  private prepareBody(body: any, headers: Headers): string | FormData | null {
    if (!body) return null;

    const contentType = headers.get("Content-Type");

    // Handle FormData
    if (body instanceof FormData) {
      headers.delete("Content-Type"); // Let browser set it
      return body;
    }

    // Handle JSON
    if (contentType?.includes("application/json")) {
      return JSON.stringify(body);
    }

    // Handle URL encoded
    if (contentType?.includes("application/x-www-form-urlencoded")) {
      const params = new URLSearchParams();
      Object.entries(body).forEach(([key, value]) => {
        params.append(key, String(value));
      });
      return params.toString();
    }

    // Default to JSON
    return JSON.stringify(body);
  }

  /**
   * Handle response parsing with metadata extraction
   */
  private async parseResponse<T>(
    response: Response,
    requestId?: string,
    startTime?: number,
  ): Promise<ApiResponse<T>> {
    const contentType = response.headers.get("Content-Type") || "";
    let data: T;

    try {
      if (contentType.includes("application/json")) {
        data = await response.json();
      } else if (contentType.includes("text/")) {
        data = (await response.text()) as T;
      } else {
        data = (await response.blob()) as T;
      }
    } catch (parseError) {
      logger.warn("Failed to parse response body", { parseError, contentType });
      data = null as T;
    }

    // Extract metadata from response
    const meta: ApiResponse<T>["meta"] = {};

    // Add timing information
    if (requestId && startTime) {
      meta.timing = {
        requestId,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString(),
      };
    }

    // Extract pagination info from headers or data
    if (typeof data === "object" && data !== null) {
      const dataObj = data as any;
      if (dataObj.pagination || dataObj.meta?.pagination) {
        meta.pagination = dataObj.pagination || dataObj.meta.pagination;
      }
    }

    // Check for pagination headers (common patterns)
    const totalHeader =
      response.headers.get("X-Total-Count") ||
      response.headers.get("Total-Count");
    const pageHeader =
      response.headers.get("X-Page") || response.headers.get("Page");
    const limitHeader =
      response.headers.get("X-Per-Page") ||
      response.headers.get("Per-Page") ||
      response.headers.get("Limit");

    if (totalHeader && pageHeader && limitHeader) {
      const total = parseInt(totalHeader, 10);
      const page = parseInt(pageHeader, 10);
      const limit = parseInt(limitHeader, 10);

      meta.pagination = {
        page,
        limit,
        total,
        totalPages: Math.ceil(total / limit),
      };
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      meta: Object.keys(meta).length > 0 ? meta : undefined,
    };
  }

  /**
   * Create and throw API error
   */
  private createError(response: Response, data: any): ApiError {
    const error: ApiError = {
      message: data?.message || response.statusText || "Request failed",
      status: response.status,
      code: data?.code || data?.error_code,
      details: data,
    };

    logger.error("HTTP request failed", {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      error,
    });

    return error;
  }

  /**
   * Main request method with timeout support
   */
  private async requestWithTimeout<T>(
    url: string,
    options: RequestInit,
    timeout: number,
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error(`Request timeout after ${timeout}ms`);
      }
      throw error;
    }
  }

  /**
   * Generic request method
   */
  async request<T = any>(
    endpoint: string,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    const {
      method = "GET",
      headers: customHeaders,
      body,
      params,
      cache,
      credentials = this.defaultCredentials,
      timeout = this.defaultTimeout,
    } = config;

    const url = this.buildURL(endpoint, params);
    const headers = this.prepareHeaders(customHeaders);
    const requestBody = this.prepareBody(body, headers);

    const requestOptions: RequestInit = {
      method,
      headers,
      body: requestBody,
      cache,
      credentials,
    };

    // logger.debug("Making HTTP request", {
    //   method,
    //   url,
    //   headers: Object.fromEntries(headers.entries()),
    //   hasBody: !!requestBody,
    // });

    const startTime = Date.now();
    const requestId = headers.get("X-Request-ID") || "unknown";

    try {
      const response = await this.requestWithTimeout(
        url,
        requestOptions,
        timeout,
      );
      const parsedResponse = await this.parseResponse<T>(
        response,
        requestId,
        startTime,
      );

      if (!response.ok) {
        throw this.createError(response, parsedResponse.data);
      }

      // logger.debug("HTTP request successful", {
      //   method,
      //   url,
      //   status: response.status,
      //   duration: Date.now() - startTime,
      //   requestId,
      // });

      return parsedResponse;
    } catch (error) {
      const duration = Date.now() - startTime;

      if (error instanceof Error && "status" in error) {
        // Add timing info to API errors
        (error as any).duration = duration;
        (error as any).requestId = requestId;
        throw error;
      }

      // Handle network and other errors
      const networkError: ApiError = {
        message: error instanceof Error ? error.message : "Network error",
        details: { error, duration, requestId },
      };

      logger.error("Network error", {
        method,
        url,
        error: networkError,
        duration,
        requestId,
      });
      throw networkError;
    }
  }

  // Convenience methods
  async get<T = any>(
    endpoint: string,
    config: Omit<RequestConfig, "method"> = {},
  ) {
    return this.request<T>(endpoint, { ...config, method: "GET" });
  }

  async post<T = any>(
    endpoint: string,
    body?: any,
    config: Omit<RequestConfig, "method" | "body"> = {},
  ) {
    return this.request<T>(endpoint, { ...config, method: "POST", body });
  }

  async put<T = any>(
    endpoint: string,
    body?: any,
    config: Omit<RequestConfig, "method" | "body"> = {},
  ) {
    return this.request<T>(endpoint, { ...config, method: "PUT", body });
  }

  async patch<T = any>(
    endpoint: string,
    body?: any,
    config: Omit<RequestConfig, "method" | "body"> = {},
  ) {
    return this.request<T>(endpoint, { ...config, method: "PATCH", body });
  }

  async delete<T = any>(
    endpoint: string,
    config: Omit<RequestConfig, "method"> = {},
  ) {
    return this.request<T>(endpoint, { ...config, method: "DELETE" });
  }
}

// Default instance
export const httpClient = new HttpClient();

// Authenticated instance (for routes requiring auth)
export const httpClientAuth = new HttpClient({
  headers: {
    "Content-Type": "application/json",
    // Auth headers will be added by interceptors if needed
  },
});

// Proxy instance (for frontend-to-frontend communication)
export const httpClientProxy = new HttpClient({
  baseURL: process.env.NEXT_PUBLIC_FRONT_URL || "http://localhost:3001",
});

// External API instance (for backend communication)
export const httpClientExternal = new HttpClient({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:3000",
});
